import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { 
  Calendar, 
  MapPin, 
  Star, 
  Loader2,
  Clock,
  XCircle,
  Pencil,
  Save,
  MessageCircle,
  CreditCard,
  FileText
} from "lucide-react";
import BookingMessages from "../components/BookingMessages";
import PaymentButtons from "../components/PaymentButtons";
import { useTranslation } from "react-i18next";
import { generateReceipt } from "../utils/generateReceipt";

import { API } from "../utils/api";

export default function MyBookings() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const [myBookings, setMyBookings] = useState([]);
  const [receivedBookings, setReceivedBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reviewData, setReviewData] = useState({ rating: 5, comment: "" });
  const [editData, setEditData] = useState({ vehicle_plate: "", notes: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/");
    }
  }, [user, authLoading, navigate]);

  const fetchBookings = useCallback(async () => {
    try {
      // 1. Sincronizar pagos pendientes con Stripe (best-effort, no bloquea)
      try {
        await axios.post(`${API}/payments/sync-pending`);
      } catch (_e) {
        // si falla la sync no bloqueamos el fetch de reservas
      }
      // 2. Cargar las reservas (ya con estados sincronizados)
      const [myRes, receivedRes] = await Promise.all([
        axios.get(`${API}/bookings/my`),
        axios.get(`${API}/bookings/received`)
      ]);
      setMyBookings(myRes.data);
      setReceivedBookings(receivedRes.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchBookings();
    }
  }, [user, fetchBookings]);

  // Refrescar bookings cuando el usuario vuelve a la pestaña/ventana
  // (típico tras volver de Stripe Checkout o de la pasarela de pago)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && user) {
        fetchBookings();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [user, fetchBookings]);

  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status?status=cancelled`);
      setMyBookings(myBookings.map(b => 
        b.id === bookingId ? { ...b, status: "cancelled" } : b
      ));
      toast.success("Reserva cancelada");
    } catch (error) {
      toast.error("Error al cancelar la reserva");
    }
  };

  const openReviewModal = (booking) => {
    setSelectedBooking(booking);
    setReviewData({ rating: 5, comment: "" });
    setReviewModalOpen(true);
  };

  const openEditModal = (booking) => {
    setSelectedBooking(booking);
    setEditData({
      vehicle_plate: booking.vehicle_plate || "",
      notes: booking.notes || ""
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedBooking) return;
    
    setSavingEdit(true);
    try {
      await axios.put(`${API}/bookings/${selectedBooking.id}`, editData);
      
      // Actualizar la reserva en la lista local
      setMyBookings(myBookings.map(b => 
        b.id === selectedBooking.id 
          ? { ...b, vehicle_plate: editData.vehicle_plate, notes: editData.notes } 
          : b
      ));
      
      toast.success("Reserva actualizada correctamente");
      setEditModalOpen(false);
    } catch (error) {
      const message = error.response?.data?.detail || "Error al actualizar la reserva";
      toast.error(message);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!selectedBooking) return;

    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, {
        parking_spot_id: selectedBooking.parking_spot_id,
        booking_id: selectedBooking.id,
        rating: reviewData.rating,
        comment: reviewData.comment || null
      });
      toast.success("¡Gracias por tu reseña!");
      setReviewModalOpen(false);
    } catch (error) {
      const message = error.response?.data?.detail || "Error al enviar la reseña";
      toast.error(message);
    } finally {
      setSubmittingReview(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      confirmed: "status-confirmed",
      pending: "status-pending",
      cancelled: "status-cancelled",
      completed: "status-confirmed"
    };
    const statusLabels = {
      confirmed: t('myBookings.status.confirmed'),
      pending: t('myBookings.status.pending'),
      cancelled: t('myBookings.status.cancelled'),
      completed: t('myBookings.status.completed')
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles.pending}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageModalBooking, setMessageModalBooking] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentModalBooking, setPaymentModalBooking] = useState(null);

  const openMessageModal = (booking) => {
    setMessageModalBooking(booking);
    setMessageModalOpen(true);
  };

  const openPaymentModal = (booking) => {
    setPaymentModalBooking(booking);
    setPaymentModalOpen(true);
  };

  const BookingCard = ({ booking, isReceived = false }) => {
    const isPendingPayment = booking.payment_status !== "paid" && booking.status !== "cancelled";
    
    return (
      <div className="dashboard-card" data-testid={`booking-${booking.id}`}>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="w-full sm:w-32 h-24 sm:h-auto rounded-lg overflow-hidden flex-shrink-0">
            <img
              src={booking.parking_spot_image || "https://images.unsplash.com/photo-1548343361-02248be15911?crop=entropy&cs=srgb&fm=jpg&q=85&w=200"}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
              <div className="min-w-0">
                <Link to={`/spot/${booking.parking_spot_id}`}>
                  <h3 className="text-lg font-semibold text-white hover:text-violet-400 transition-colors truncate">
                    {booking.parking_spot_title}
                  </h3>
                </Link>
                <div className="flex items-center gap-1 text-gray-400 text-sm">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{booking.parking_spot_address}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isPendingPayment && !isReceived && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30 whitespace-nowrap">
                    {t('myBookings.paymentPending')}
                  </span>
                )}
                {booking.payment_status === "paid" && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30 whitespace-nowrap">
                    {t('myBookings.paid')}
                  </span>
                )}
                {getStatusBadge(booking.status)}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-400 mb-3">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(booking.start_date)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>
                  {booking.booking_type === "hour" ? t('booking.byHour') : 
                   booking.booking_type === "day" ? t('booking.byDay') : t('booking.byMonth')}
                </span>
              </div>
              {isReceived && booking.user_name && (
                <div className="text-violet-400">
                  {t('myBookings.client')}: {booking.user_name}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <div className="price-display text-xl text-white">
                €{(isReceived ? booking.total_price : booking.total_price * 1.10).toFixed(2)}
                {!isReceived && (
                  <span className="text-xs text-gray-500 font-normal ml-2">
                    (incl. 10% comisión)
                  </span>
                )}
              </div>
              
              {/* Responsive button grid - 2 columns on mobile, flex on desktop */}
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                {/* Messages button - always visible */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openMessageModal(booking)}
                  className="border-violet-500/20 text-violet-400 hover:bg-violet-500/10 text-xs h-9"
                  data-testid={`messages-btn-${booking.id}`}
                >
                  <MessageCircle className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                  <span className="truncate">{t('myBookings.messages')}</span>
                </Button>

                {/* Receipt download button - always visible, shows DRAFT if not paid */}
                {!isReceived && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const result = await generateReceipt(booking, user);
                        if (result) {
                          toast.success(booking.payment_status === "paid" 
                            ? "Factura descargada correctamente" 
                            : "Borrador descargado"
                          );
                        }
                      } catch (error) {
                        console.error("Error generating receipt:", error);
                        toast.error("Error al generar el recibo: " + error.message);
                      }
                    }}
                    className={`text-xs h-9 ${
                      booking.payment_status === "paid"
                        ? "border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10"
                        : "border-gray-500/20 text-gray-400 hover:bg-gray-500/10"
                    }`}
                    data-testid={`receipt-btn-${booking.id}`}
                  >
                    <FileText className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                    <span className="truncate">
                      {booking.payment_status === "paid" ? "Factura" : "Borrador"}
                    </span>
                  </Button>
                )}

                {/* Payment button - only for tenant with pending payment */}
                {!isReceived && isPendingPayment && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openPaymentModal(booking)}
                    className="border-green-500/20 text-green-400 hover:bg-green-500/10 text-xs h-9"
                    data-testid={`payment-btn-${booking.id}`}
                  >
                    <CreditCard className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                    <span className="truncate">{t('myBookings.pay')}</span>
                  </Button>
                )}

                {!isReceived && booking.status === "confirmed" && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(booking)}
                      className="border-blue-500/20 text-blue-400 hover:bg-blue-500/10 text-xs h-9"
                      data-testid={`edit-btn-${booking.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                      <span className="truncate">{t('myBookings.editBooking')}</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openReviewModal(booking)}
                      className="border-amber-500/20 text-amber-400 hover:bg-amber-500/10 text-xs h-9"
                      data-testid={`review-btn-${booking.id}`}
                    >
                      <Star className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                      <span className="truncate">{t('myBookings.rate')}</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancelBooking(booking.id)}
                      className="border-red-500/20 text-red-400 hover:bg-red-500/10 text-xs h-9 col-span-2 sm:col-span-1"
                      data-testid={`cancel-btn-${booking.id}`}
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                      <span className="truncate">{t('myBookings.cancelBooking')}</span>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="pt-20 min-h-screen" data-testid="my-bookings-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">{t('myBookings.title')}</h1>
          <p className="text-gray-400">{t('myBookings.subtitle')}</p>
        </div>

        <Tabs defaultValue="my" className="w-full">
          <TabsList className="bg-[#231f30] border-white/10 mb-6">
            <TabsTrigger 
              value="my" 
              className="data-[state=active]:bg-violet-500 data-[state=active]:text-white"
              data-testid="my-bookings-tab"
            >
              {t('myBookings.made')} ({myBookings.length})
            </TabsTrigger>
            <TabsTrigger 
              value="received"
              className="data-[state=active]:bg-violet-500 data-[state=active]:text-white"
              data-testid="received-bookings-tab"
            >
              {t('myBookings.received')} ({receivedBookings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="my">
            {myBookings.length > 0 ? (
              <div className="space-y-4">
                {myBookings.map((booking) => (
                  <BookingCard key={booking.id} booking={booking} />
                ))}
              </div>
            ) : (
              <div className="empty-state dashboard-card">
                <div className="empty-state-icon">
                  <Calendar className="w-10 h-10 text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{t('myBookings.noBookings')}</h3>
                <p className="text-gray-400 mb-6">
                  {t('myBookings.startBooking')}
                </p>
                <Link to="/explore">
                  <Button className="bg-violet-500 hover:bg-violet-600 text-white">
                    {t('explore.title')}
                  </Button>
                </Link>
              </div>
            )}
          </TabsContent>

          <TabsContent value="received">
            {receivedBookings.length > 0 ? (
              <div className="space-y-4">
                {receivedBookings.map((booking) => (
                  <BookingCard key={booking.id} booking={booking} isReceived />
                ))}
              </div>
            ) : (
              <div className="empty-state dashboard-card">
                <div className="empty-state-icon">
                  <Calendar className="w-10 h-10 text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{t('myBookings.noReceivedBookings')}</h3>
                <p className="text-gray-400 mb-6">
                  {t('myBookings.receivedSubtitle')}
                </p>
                <Link to="/add-spot">
                  <Button className="bg-violet-500 hover:bg-violet-600 text-white">
                    {t('nav.addSpot')}
                  </Button>
                </Link>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Review Modal */}
      <Dialog open={reviewModalOpen} onOpenChange={setReviewModalOpen}>
        <DialogContent className="bg-[#231f30] border-white/10 sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">{t('myBookings.leaveReview')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-300 mb-2 block">{t('myBookings.score')}</Label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => setReviewData(prev => ({ ...prev, rating }))}
                    className="focus:outline-none"
                    data-testid={`rating-star-${rating}`}
                  >
                    <Star 
                      className={`w-8 h-8 transition-colors ${
                        rating <= reviewData.rating 
                          ? "fill-amber-400 text-amber-400" 
                          : "text-gray-600 hover:text-amber-400"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-gray-300 mb-2 block">{t('myBookings.comment')}</Label>
              <Textarea
                placeholder={t('myBookings.commentPlaceholder')}
                value={reviewData.comment}
                onChange={(e) => setReviewData(prev => ({ ...prev, comment: e.target.value }))}
                className="bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                rows={4}
                data-testid="review-comment-input"
              />
            </div>

            <Button
              className="w-full bg-violet-500 hover:bg-violet-600 text-white"
              onClick={handleSubmitReview}
              disabled={submittingReview}
              data-testid="submit-review-btn"
            >
              {submittingReview ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('myBookings.sending')}
                </>
              ) : (
                t('myBookings.sendReview')
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Booking Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-[#231f30] border-white/10 sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">{t('myBookings.editBookingTitle')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {selectedBooking && (
              <div className="bg-[#15121e] rounded-lg p-3 border border-white/10">
                <p className="text-white font-medium">{selectedBooking.parking_spot_title}</p>
                <p className="text-gray-400 text-sm">{selectedBooking.parking_spot_address}</p>
              </div>
            )}

            <div>
              <Label className="text-gray-300 mb-2 block">{t('booking.vehiclePlate')}</Label>
              <Input
                type="text"
                placeholder={t('myBookings.vehiclePlatePlaceholder')}
                value={editData.vehicle_plate}
                onChange={(e) => setEditData(prev => ({ ...prev, vehicle_plate: e.target.value }))}
                className="bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                data-testid="edit-vehicle-plate-input"
              />
            </div>

            <div>
              <Label className="text-gray-300 mb-2 block">{t('booking.notes')}</Label>
              <Textarea
                placeholder={t('myBookings.notesPlaceholder')}
                value={editData.notes}
                onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-[#15121e] border-white/10 text-white placeholder:text-gray-500"
                rows={3}
                data-testid="edit-notes-input"
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setEditModalOpen(false)}
                className="flex-1 border-white/10 text-gray-300 hover:bg-white/5"
              >
                {t('common.cancel')}
              </Button>
              <Button
                className="flex-1 bg-violet-500 hover:bg-violet-600 text-white"
                onClick={handleSaveEdit}
                disabled={savingEdit}
                data-testid="save-edit-btn"
              >
                {savingEdit ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('myBookings.saving')}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {t('common.save')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Messages Modal */}
      <Dialog open={messageModalOpen} onOpenChange={setMessageModalOpen}>
        <DialogContent className="bg-[#231f30] border-white/10 sm:max-w-[500px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-violet-400" />
              {t('myBookings.messages')}
            </DialogTitle>
            {messageModalBooking && (
              <p className="text-sm text-gray-400 mt-1">
                {messageModalBooking.parking_spot_title}
              </p>
            )}
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden mt-4">
            {messageModalBooking && (
              <BookingMessages 
                bookingId={messageModalBooking.id}
                ownerName={messageModalBooking.owner_name}
                tenantName={messageModalBooking.user_name}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
        <DialogContent className="bg-[#231f30] border-white/10 sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-400" />
              {t('payment.title')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            {paymentModalBooking && (
              <>
                <div className="bg-[#15121e] rounded-lg p-3 border border-white/10 mb-4">
                  <p className="text-white font-medium">{paymentModalBooking.parking_spot_title}</p>
                  <p className="text-gray-400 text-sm">{paymentModalBooking.parking_spot_address}</p>
                </div>
                <PaymentButtons 
                  bookingId={paymentModalBooking.id} 
                  basePrice={paymentModalBooking.total_price}
                  onPaymentSuccess={() => {
                    toast.success("¡Pago completado!");
                    fetchBookings();
                    setPaymentModalOpen(false);
                  }}
                  onAlreadyPaid={() => {
                    toast.info("Esta reserva ya estaba pagada. Actualizando...");
                    fetchBookings();
                    setPaymentModalOpen(false);
                  }}
                />
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
